<route lang="json">
{ "name": "opportunities" }
</route>

<template>
  <div>
    <ContentEditorInsert v-model="content" />
    <div class="join-bg">
      <div class="row1 flex-row">
        <img
          :srcset="boxMarbleImage[0].srcset"
          sizes="748px"
          alt="Paparazzi Accessories box delivered for consultants sitting on a kitchen counter"
        />
        <img
          :srcset="notebookImage[0].srcset"
          sizes="328px"
          alt="Paparazzi Accessories consultant notebook and pen stationary set"
        />
      </div>
      <div class="flex-row box-join">
        <img
          class="box-image"
          :srcset="boxImage[0].srcset"
          sizes="100vw"
          alt="Paparazzi Accessories consultant box delivered to your home with text saying Let's get this party started"
        />
        <div class="flex-column flex-center join-step1">
          <RouterLink :to="{ name: 'opportunities-signup' }">
            <Button class="pink">Join the Team</Button>
          </RouterLink>
          <h1 class="become" v-html="content.become_header" />
          <p v-html="content.become_p1" />
          <p v-html="content.become_p2"></p>
        </div>
      </div>
      <div class="join-step2">
        <img
          :srcset="onCouchImage[0].srcset"
          sizes="(max-width: 740px) 100vw, 740px"
          alt="Woman sitting on sofa planning her Paparazzi Accessories party with her consultant box and stationary"
        />
        <h2 class="text-center">Party your way</h2>
        <div class="flex-row gap-sm">
          <div class="card flex-center flex1">
            <h3 v-html="content.card1_title" />
            <p v-html="content.card1_body" />
          </div>
          <div class="card flex-center flex1">
            <h3 v-html="content.card2_title" />
            <p v-html="content.card2_body" />
          </div>
          <div class="card flex-center flex1">
            <h3 v-html="content.card3_title" />
            <p v-html="content.card3_body" />
          </div>
        </div>
      </div>
      <div class="join-step3">
        <h2 v-html="content.reasons_header" />
        <div class="flex-row">
          <img
            class="couch-image"
            :srcset="onCouchImage[0].srcset"
            alt="Woman sitting on sofa planning her Paparazzi Accessories party with her consultant box and stationary"
          />
          <div class="flex-column">
            <div class="flex1">
              <h3 v-html="content.reason1_title" />
              <div class="line"></div>
              <p v-html="content.reason1_body" />
            </div>
            <div class="flex2">
              <h3 v-html="content.reason2_title" />
              <div class="line"></div>
              <p v-html="content.reason2_body" />
            </div>
            <div class="flex3">
              <h3 v-html="content.reason3_title" />
              <div class="line"></div>
              <p v-html="content.reason3_body" />
              <RouterLink :to="{ name: 'opportunities-signup' }">
                <Button class="pink">Join</Button>
              </RouterLink>
            </div>
          </div>
          <img
            class="couch-image2"
            :srcset="notebookImage[0].srcset"
            sizes="100vw"
            alt="Woman sitting on sofa planning her Paparazzi Accessories party with her consultant box and stationary"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import onCouchImage from "@/assets/img/man-woman-looking-at-open-paparazzi-consultant-box.jpg?preset=thumbnail";
import boxMarbleImage from "@/assets/img/paparazzi-accessories-consultant-box-delivered-kitchen.jpeg?preset=thumbnail";
import boxImage from "@/assets/img/paparazzi-accessories-consultant-planning-party.jpg?preset=thumbnail";
import notebookImage from "@/assets/img/paparazzi-accessories-consultant-stationary-notebook-pen.jpg?preset=thumbnail";
import ContentEditorInsert from "@/components/ContentEditorInsert.vue";
import { useHead } from "@unhead/vue";
import Button from "primevue/button";
import { ref } from "vue";

const content = ref({
  become_header: "Become a Consultant Today!",
  become_p1: `Join the Paparazzi Party today and unlock a future that sparkles with possibilities!`,
  become_p2: `Step 1: Select an Enrollment Option<br />Step 2: Choose Your Sponsor`,
  card1_title: "Paint Your Town Pink",
  card1_body: `Purchase adorable jewelry at wholesale pricing and sell it directly to friends, family, followers, neighbors, co-workers, and more!`,
  card2_title: `Build a Team`,
  card2_body: `Invite others to join the Paparazzi Party and receive commissions from the sales they generate.`,
  card3_title: `Retail`,
  card3_body: `Receive a personalized replicated website where your customers can shop 24/7. These orders ship directly from our warehouse and you receive a retail profit on every accessory they buy.`,
  reasons_header: `Reasons to join the Paparazzi party`,
  reason1_title: `It’s so much more than<br />Just Jewelry`,
  reason1_body: `Paparazzi Accessories are a catalyst for curating confidence. As a Consultant, you get to help people discover the transformational power of self-confidence that comes from feeling like a million without breaking the bank.`,
  reason2_title: `Sell Anywhere, Anytime`,
  reason2_body: `Set a schedule for sales that supports your lifestyle and throw parties online or in your living room, and manage a team of like-minded accessory aficionados.`,
  reason3_title: `Commissions`,
  reason3_body: `It's simple: Buy inventory at wholesale pricing and sell the accessories at their famously affordable price. With new designs dropping daily, there is no limit to the jewelry joy you can bring!
    <a
      class="pa-pink"
      target="_system"
      href="https://extranet.securefreedom.com/Paparazzi/Personal/Resources/Comp_Plan_2025_ENG.pdf"
    >
      Learn more
    </a> about earning commissions as a Paparazzi Consultant.`,
});

useHead({
  title: "Join Us",
  meta: [
    {
      name: "description",
      content:
        "Join Paparazzi Accessories to become your own boss & learn from jewelry enthusiasts & business owners. Become a Paparazzi consultant today!",
    },
  ],
  link: [
    {
      rel: "canonical",
      href: "https://paparazziaccessories.com/opportunities",
    },
  ],
});
</script>

<style scoped>
.page {
  margin-bottom: var(--1XL, 64px);
}
.row1 {
  height: 440px;

  & > img {
    object-fit: cover;
  }
  & > img:first-child {
    width: calc(100% * (738 / 1120));
  }
  & > img:last-child {
    width: calc(100% * (382 / 1120));
  }
}
.box-image {
  width: 603px;
  height: 400px;
  margin-top: -72px;
  margin-left: var(--XMd);
  border: 4px solid white;
}

.join-step1 {
  padding: var(--3XL, 80px);
  a {
    text-align: center;
  }
  button.pink {
    padding: var(--2XS, 8px) 10px;
    font-size: 24px;
    font-weight: 300;
  }
  p {
    margin-top: 0;
  }
  h1,
  :deep(h1) {
    font-size: 16px;
    text-align: center;
    margin: var(--Sm, 24px) 0;
    font-weight: 400;
  }
}

.join-step2 {
  padding: var(--Sm, 24px);
  img {
    display: none;
  }
  h2 {
    font-size: 24px;
    text-transform: uppercase;
    text-align: center;
    font-weight: 300;
    margin: var(--Sm, 24px) 0 var(--XS, 16px);
    line-height: 1;
  }
  .card {
    padding: var(--Sm, 24px);
    background-color: var(--White, #fff);
    h3 {
      text-align: center;
      margin: 0 0 var(--XS, 16px);
      font-weight: 300;
      font-size: 18px;
      text-transform: uppercase;
    }
    p {
      margin-bottom: 0;
    }
  }
}

.join-step3 {
  h2 {
    background-color: var(--Paparazzi-Pink, #ec008c);
    margin: 0;
    padding: var(--Sm, 24px);
    font-weight: 300;
    font-size: 24px;
    text-transform: uppercase;
    color: var(--White, #fff);
    text-align: left;
  }
  :deep(.flex1) {
    background-color: var(--30, #c4c4c4);
  }
  :deep(.flex2) {
    background-color: #d8d8d8;
  }
  :deep(.flex1),
  :deep(.flex2),
  :deep(.flex3) {
    padding: var(--Sm, 24px);
    h3 {
      margin: 0 0 var(--XS, 16px) 0;
      font-size: 18px;
      font-weight: 300;
      text-transform: uppercase;
      text-align: left;
    }
    p {
      margin: 0;
    }
  }
  a {
    text-align: right;
  }
  button.pink {
    padding: var(--2XS, 8px) 10px;
    float: right;
  }
  :deep(.flex3) {
    p {
      padding-bottom: 12px;
    }
  }
}
.line {
  width: 50px;
  height: 0px;
  border-top: 1px solid #000;
  margin-bottom: var(--XS, 16px);
}

.couch-image {
  width: 740px;
  height: 632px;
  object-fit: cover;
}
.couch-image2 {
  display: none;
}

@media (max-width: 1120px) and (min-width: 901px) {
  .couch-image {
    width: 55%;
  }
  .box-image {
    width: 508px;
    object-fit: cover;
  }
  .join-step1 {
    padding: var(--XL, 56px);
  }
}
@media (max-width: 900px) and (min-width: 801px) {
  .couch-image {
    width: 50%;
  }
  .box-image {
    width: 40%;
    object-fit: cover;
  }
  .join-step1 {
    padding: var(--XL, 56px);
  }
}

@media (max-width: 1120px) and (min-width: 801px) {
  .row1 {
    height: 300px;
  }
  .join-step3 {
    :deep(.flex3) {
      p {
        margin-bottom: var(--2XS, 8px);
      }
      h3 {
        margin-bottom: var(--XS, 16px);
      }
      .line {
        margin-bottom: var(--XS, 16px);
      }
    }
  }
}

@media screen and (max-width: 800px) {
  .page {
    margin: 0;
  }
  .row1 {
    display: none !important;
  }
  .box-join {
    display: block !important;
  }
  .box-image {
    width: 100%;
    height: 400px;
    margin: 0;
    border: none;
    object-fit: cover;
  }
  .join-step1 {
    padding: var(--1XL, 64px);
  }
  .join-step2 {
    padding-top: 0;
    overflow: hidden;
    img {
      display: block;
      width: 100vw;
      height: 400px;
      background-size: cover;
      object-fit: cover;
      margin-left: calc(-1 * var(--Sm));
    }
    h3 {
      margin: var(--Sm, 24px) 0 var(--XS, 16px);
    }
    .card {
      margin-bottom: var(--Sm, 24px);
      min-height: 171px;
    }
    .card:last-child {
      margin-bottom: 0;
    }
  }
  .join-step3 {
    overflow: hidden;
    h2 {
      text-align: center;
    }
    h3 {
      text-align: left;
    }
    :deep(.flex3) {
      h4,
      .line {
        margin-bottom: var(--XS, 16px);
      }
    }
  }
  .join-step3,
  .join-step2 {
    .flex-row {
      display: block !important;
    }
  }
  .couch-image2 {
    display: block;
    width: 100vw;
    height: 400px;
    object-fit: cover;
  }
  .couch-image {
    display: none;
  }
}
@media screen and (max-width: 800px) and (min-width: 601px) {
  .join-step2 {
    .card {
      min-height: 140px;
    }
  }
}

@media screen and (max-width: 600px) {
  .card {
    min-height: 171px;
  }
}
</style>
